import type { BeatLineViteConfig } from '../api/types';

function truthy(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'yes' || value === 'on';
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function providerIcon(provider: string): string {
  if (provider === 'google') {
    return `<svg viewBox="0 0 48 48" focusable="false" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10 0 19-7.3 19-20 0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.1 5.2C38.8 39.1 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>`;
  }
  if (provider === 'facebook') {
    return `<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <circle fill="#ffffff" cx="12" cy="12" r="10" />
      <path fill="#1877F2" d="M13.3 19v-6h2l.3-2.3h-2.3V9.2c0-.7.2-1.1 1.1-1.1h1.2V6.1c-.2 0-1-.1-1.8-.1-1.8 0-3 1.1-3 3.1v1.7H8.8V13h2v6h2.5z" />
    </svg>`;
  }
  return '';
}

function installQrVisibility(config: BeatLineViteConfig): void {
  const showQrMode = truthy(config.beatlineSettings?.showQrMode ?? 1);
  const wrap = document.getElementById('qr-mode-wrap');
  const checkbox = document.getElementById('qr-mode-enable') as HTMLInputElement | null;
  if (!showQrMode) {
    if (checkbox) checkbox.checked = false;
    wrap?.remove();
    return;
  }
  if (wrap) wrap.dataset.backendEnabled = '1';
}

function authButtonsHtml(config: BeatLineViteConfig): string {
  const auth = config.beatlineSettings?.auth || {};
  const isNative = !!config.nativeApp;
  const buttons = Array.isArray(auth.socialButtons) ? auth.socialButtons : [];
  const validButtons = buttons.filter((button) => {
    const provider = String(button.provider || '').trim();
    return provider === 'google' || provider === 'facebook';
  });
  const accountUrl = escapeHtml(auth.accountUrl || '/account/');

  if (truthy(auth.loggedIn) || config.userLoggedIn) {
    const name = escapeHtml(auth.displayName || 'gebruiker');
    const logout = escapeHtml(auth.logoutUrl || '#');
    return `<div class="beatline-auth-loggedin">
      <span class="auth-user">Ingelogd als <strong>${name}</strong></span>
      <a class="auth-logout" href="${logout}">(Uitloggen)</a>
    </div>`;
  }

  const social = validButtons.length ? `<div class="beatline-nsl-wrapper beatline-nsl-wrapper--custom ${isNative ? 'beatline-nsl-wrapper--native-app beatline-nsl-wrapper--auth-gate' : 'beatline-nsl-wrapper--web'}">
    ${!isNative ? `<p class="nsl-label">Log in met:<button type="button" class="nsl-why-btn" data-open-auth-explainer="1">Waarom registreren?</button></p>` : ''}
    <div class="beatline-social-login-buttons">
      ${validButtons.map((button) => {
        const provider = String(button.provider || '').trim();
        const nativeAppLink = String(button.appLink || `com.beatlinegame.app://auth-start?provider=${encodeURIComponent(provider)}`);
        const rawHref = isNative ? '#' : String(button.fallbackUrl || auth.loginUrl || accountUrl);
        const href = escapeHtml(rawHref);
        const label = escapeHtml(button.label || (provider === 'google' ? 'Log in met Google' : 'Log in met Facebook'));
        // C40: in the locally bundled app every Google/Facebook login must go through
        // the Android native bridge. Falling back to loginSocial directly loses the
        // native auth state and leaves the user in the browser after OAuth.
        const nativeAttrs = isNative
          ? ` data-native-social-auth="1" data-provider="${escapeHtml(provider)}" data-native-app-link="${escapeHtml(nativeAppLink)}"`
          : '';
        return `<a class="beatline-social-login-btn beatline-social-login-btn--${escapeHtml(provider)}" href="${href}"${nativeAttrs}>
          <span class="beatline-social-login-btn__icon" aria-hidden="true">${providerIcon(provider)}</span>
          <span class="beatline-social-login-btn__label">${label}</span>
          <span class="beatline-social-login-btn__spacer" aria-hidden="true"></span>
        </a>`;
      }).join('')}
    </div>
  </div>` : '';

  if (isNative) {
    return `<div id="beatline-app-auth-gate" class="beatline-auth-gate is-dismissed" role="dialog" aria-modal="true" aria-hidden="true" aria-labelledby="beatline-auth-gate-title">
      <div class="beatline-auth-gate__backdrop" aria-hidden="true"></div>
      <div class="beatline-auth-gate__panel">
        <button type="button" class="beatline-auth-gate__close" data-beatline-auth-gate-close="1" aria-label="Loginvenster sluiten"><span aria-hidden="true">&times;</span></button>
        <div class="beatline-auth-gate__eyebrow">Gratis account</div>
        <h2 id="beatline-auth-gate-title" class="beatline-auth-gate__title">Log in om BeatLine te spelen</h2>
        <p class="beatline-auth-gate__text">Koppel je account zodat je playlists, voortgang en online functies goed werken.</p>
        ${social}
        <a href="${accountUrl}" class="beatline-auth-email-btn">Registreer/login met e-mailadres</a>
        <div class="beatline-auth-gate__why"><h3>Waarom registreren?</h3><ul><li>💾 Playlists worden bewaard, dus nooit dezelfde liedjes!</li><li>🎮 Online spelen met anderen</li><li>⭐ Krijg extra playlists</li><li>🔐 Gratis account</li></ul></div>
      </div>
    </div>`;
  }

  return `${social}
    <a href="${accountUrl}" class="nsl-why-btn">Registreer/login met e-mailadres</a>
    <div id="beatline-auth-explainer" class="nsl-modal" aria-hidden="true">
      <div class="nsl-modal-backdrop" data-close-auth-explainer="1"></div>
      <div class="nsl-modal-content" role="dialog" aria-modal="true" aria-labelledby="auth-explainer-title">
        <h3 id="auth-explainer-title">Waarom registreren?</h3>
        <ul class="nsl-modal-list"><li>💾 Playlists worden bewaard, dus nooit dezelfde liedjes!</li><li>🎮 Online spelen met anderen</li><li>⭐ Krijg extra playlists</li><li>🔐 Gratis account</li></ul>
        <div class="nsl-modal-actions"><button type="button" class="nsl-modal-close" data-close-auth-explainer="1">Begrepen</button></div>
      </div>
    </div>`;
}

function installAuthMarkup(config: BeatLineViteConfig): void {
  if (document.querySelector('.beatline-auth-loggedin, .beatline-nsl-wrapper--web, #beatline-app-auth-gate')) return;
  const html = authButtonsHtml(config).trim();
  if (!html) return;
  const anchor = document.querySelector('.start-actions');
  if (anchor) {
    anchor.insertAdjacentHTML('afterend', html);
  } else {
    document.getElementById('start-content')?.insertAdjacentHTML('beforeend', html);
  }

  const profile = document.getElementById('start-profile-btn') as HTMLElement | null;
  const lang = document.getElementById('start-lang-btn') as HTMLElement | null;
  const loggedIn = truthy(config.beatlineSettings?.auth?.loggedIn) || !!config.userLoggedIn;
  if (profile) profile.style.display = loggedIn ? '' : 'none';
  if (lang) lang.style.display = loggedIn ? 'none' : '';
}



function installAuthGateController(config: BeatLineViteConfig): void {
  const authGate = document.getElementById('beatline-app-auth-gate') as HTMLElement | null;
  if (!authGate) return;

  // Move fixed modal out of transformed app containers, same as the original plugin inline script.
  try {
    if (document.body && authGate.parentNode !== document.body) document.body.appendChild(authGate);
  } catch (_) {}

  authGate.querySelectorAll<HTMLAnchorElement>('[data-native-social-auth="1"]').forEach((link) => {
    const appLink = link.getAttribute('data-native-app-link') || '';
    if (appLink) link.setAttribute('href', appLink);
  });

  const title = authGate.querySelector('#beatline-auth-gate-title') as HTMLElement | null;
  const text = authGate.querySelector('.beatline-auth-gate__text') as HTMLElement | null;
  const eyebrow = authGate.querySelector('.beatline-auth-gate__eyebrow') as HTMLElement | null;
  const defaultTitle = title?.textContent || '';
  const defaultText = text?.textContent || '';
  const defaultEyebrow = eyebrow?.textContent || '';

  const showAuthGate = (mode?: string): void => {
    const memberMode = String(mode || '') === 'member';
    if (title) title.textContent = memberMode ? 'Log in om Member te worden' : defaultTitle;
    if (text) text.textContent = memberMode ? 'Maak gratis een account aan of log in. Daarna kun je BeatLine Member activeren.' : defaultText;
    if (eyebrow) eyebrow.textContent = memberMode ? 'Account nodig' : defaultEyebrow;
    authGate.classList.remove('is-dismissed');
    authGate.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('beatline-auth-gate-active');
    document.body?.classList.add('beatline-auth-gate-active');
    try { (authGate.querySelector('.beatline-social-login-btn, .beatline-auth-email-btn, [data-beatline-auth-gate-close]') as HTMLElement | null)?.focus({ preventScroll: true }); } catch (_) {}
  };

  const closeAuthGate = (): void => {
    try {
      const active = document.activeElement as HTMLElement | null;
      if (active && authGate.contains(active)) active.blur();
    } catch (_) {}
    authGate.classList.add('is-dismissed');
    authGate.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('beatline-auth-gate-active');
    document.body?.classList.remove('beatline-auth-gate-active');
  };

  (window as any).beatlineShowAuthGate = showAuthGate;
  authGate.querySelectorAll('[data-beatline-auth-gate-close="1"], .beatline-auth-gate__backdrop').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      closeAuthGate();
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !authGate.classList.contains('is-dismissed')) closeAuthGate();
  });

  const loggedIn = truthy(config.beatlineSettings?.auth?.loggedIn) || !!config.userLoggedIn;
  if (config.nativeApp && !loggedIn) {
    // In native/app context the login gate must be visible immediately for logged-out users.
    showAuthGate('default');
  }
}

function firstSubscriptionTier(config: BeatLineViteConfig): Record<string, any> | null {
  const billing = config.beatlineSettings?.billing || {};
  const tiers = billing.subscriptionTiers;
  if (!tiers) return null;
  if (Array.isArray(tiers)) {
    return tiers.find((tier) => tier && typeof tier === 'object') || null;
  }
  if (typeof tiers === 'object') {
    const preferred = (tiers as Record<string, any>)['1'];
    if (preferred && typeof preferred === 'object') return preferred;
    return Object.values(tiers as Record<string, any>).find((tier) => tier && typeof tier === 'object') as Record<string, any> || null;
  }
  return null;
}

function firstString(obj: Record<string, any> | null, keys: string[]): string {
  if (!obj) return '';
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return String(value).trim();
  }
  return '';
}

function firstNumber(obj: Record<string, any> | null, keys: string[]): number {
  const raw = firstString(obj, keys);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

function buildWebBuyUrl(config: BeatLineViteConfig, wooProductId: number, tier: Record<string, any> | null): string {
  const explicit = firstString(tier, ['web_buy_url', 'webBuyUrl', 'buy_url', 'buyUrl', 'checkout_url', 'checkoutUrl', 'url']);
  if (explicit) return explicit;
  if (wooProductId > 0) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('beatline_buy', String(wooProductId));
      const buyNonce = String((config.beatlineSettings as any)?.buyNonce || '');
      if (buyNonce) url.searchParams.set('_wpnonce', buyNonce);
      return url.toString();
    } catch (_) {}
  }
  return '';
}

function ensureMemberBuyButton(
  modal: HTMLElement,
  webBuyUrl: string,
  active: boolean,
  config: BeatLineViteConfig,
  productId: string,
  level: number
): void {
  let buyBtn = document.getElementById('beatline-member-buy-btn') as HTMLButtonElement | null;
  if (active) {
    buyBtn?.remove();
    return;
  }
  if (!webBuyUrl && !productId) return;
  if (!buyBtn) {
    buyBtn = document.createElement('button');
    buyBtn.id = 'beatline-member-buy-btn';
    buyBtn.type = 'button';
    buyBtn.className = 'beatline-member-modal__buy';
    buyBtn.textContent = 'Member worden';
    const status = modal.querySelector('.beatline-member-modal__status');
    const promo = document.getElementById('beatline-member-promo-form');
    const panel = modal.querySelector('.beatline-member-modal__panel');
    if (status && status.parentNode) status.parentNode.insertBefore(buyBtn, status);
    else if (promo && promo.parentNode) promo.parentNode.insertBefore(buyBtn, promo);
    else panel?.appendChild(buyBtn);
  }

  // beatline_member_button_native_billing_v1
  // Koppel de knop aan Google Play Billing in de Android app.
  // Op web valt hij terug naar de Woo/web-buy URL.
  buyBtn.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const w = window as any;
    const nativeBilling = w.BeatLineAndroidBilling;
    const billing = (config.beatlineSettings?.billing || {}) as Record<string, any>;
    const accountRef = String(billing.accountRef || '');

    const isNativeContext = !!(
      config.nativeApp ||
      w.Capacitor ||
      w.BeatLineNative ||
      nativeBilling
    );

    if (
      isNativeContext &&
      productId &&
      nativeBilling &&
      typeof nativeBilling.startSubscription === 'function'
    ) {
      nativeBilling.startSubscription(String(productId), String(level || 1), accountRef);
      return;
    }

    if (
      isNativeContext &&
      productId &&
      nativeBilling &&
      typeof nativeBilling.startPurchase === 'function'
    ) {
      nativeBilling.startPurchase(String(productId), 'subscription:' + String(level || 1), accountRef);
      return;
    }

    if (webBuyUrl) {
      window.location.href = webBuyUrl;
      return;
    }

    const toast = w.onlineShowToast;
    if (typeof toast === 'function') {
      toast('Member worden is nog niet beschikbaar.');
    } else {
      alert('Member worden is nog niet beschikbaar.');
    }
  };
}

function installMemberModalConfig(config: BeatLineViteConfig): void {
  const modal = document.getElementById('beatline-member-modal') as HTMLElement | null;
  if (!modal) return;

  const billing = (config.beatlineSettings?.billing || {}) as Record<string, any>;
  const tier = firstSubscriptionTier(config);
  const level = parseInt(String(billing.subscriptionLevel || firstString(tier, ['level', 'subscription_level', 'subscriptionLevel']) || '1'), 10) || 1;
  const productId = firstString(tier, ['product_id', 'productId', 'google_product_id', 'googleProductId', 'subscription_product_id', 'subscriptionProductId']);
  const wooProductId = firstNumber(tier, ['woo_product_id', 'wooProductId', 'woocommerce_product_id', 'woocommerceProductId', 'product', 'id']);
  const webBuyUrl = buildWebBuyUrl(config, wooProductId, tier);
  const active = Number(billing.subscriptionLevel || 0) >= level && level > 0;
  const details = ((billing as any).subscriptionDetails && typeof (billing as any).subscriptionDetails === 'object') ? (billing as any).subscriptionDetails as Record<string, any> : null;
  const expiryText = firstString(tier, ['expiry_text', 'expiryText', 'expires_text', 'expiresText'])
    || String((billing as any).subscriptionExpiryText || '')
    || firstString(details, ['expiry_text', 'expiryText', 'expires_text', 'expiresText']);

  modal.setAttribute('data-member-level', String(level));
  modal.setAttribute('data-member-product-id', productId);
  modal.setAttribute('data-member-woo-product-id', String(wooProductId || 0));
  modal.setAttribute('data-member-web-buy-url', webBuyUrl);
  modal.setAttribute('data-member-active', active ? '1' : '0');
  modal.setAttribute('data-member-context', config.nativeApp ? 'app' : 'web');
  if (expiryText) modal.setAttribute('data-member-expiry-text', expiryText);

  const status = modal.querySelector('.beatline-member-modal__status') as HTMLElement | null;
  if (active) {
    if (status) {
      status.className = 'beatline-member-modal__status is-active';
      status.innerHTML = 'Je bent al Member ✅' + (expiryText ? `<span class="beatline-member-modal__status-expiry">Actief tot en met: ${escapeHtml(expiryText)}</span>` : '');
    }
  } else if (status && status.classList.contains('is-warning')) {
    if (webBuyUrl || productId || wooProductId > 0) {
      status.remove();
    } else if (!truthy(billing.subscriptionsEnabled) && !truthy(billing.enabled)) {
      status.remove();
    } else {
      status.textContent = config.nativeApp
        ? 'Member is nog niet gekoppeld aan een Google Play product ID.'
        : 'Member is nog niet gekoppeld aan een WooCommerce product.';
    }
  }

  ensureMemberBuyButton(modal, webBuyUrl, active, config, productId, level);
}

function installMemberModalFocusSafety(): void {
  const modal = document.getElementById('beatline-member-modal') as HTMLElement | null;
  if (!modal) return;

  const moveFocusOut = (): void => {
    try {
      const active = document.activeElement as HTMLElement | null;
      if (active && modal.contains(active)) {
        active.blur();
      }
    } catch (_) {}

    try {
      const fallback = document.getElementById('start-member-cart-btn')
        || document.getElementById('start-info-btn')
        || document.getElementById('start-screen')
        || document.getElementById('beatline-app')
        || document.body;
      if (fallback instanceof HTMLElement) {
        if (!fallback.hasAttribute('tabindex') && fallback !== document.body) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    } catch (_) {}
  };

  // The original inline member-modal script closes the modal during bubble phase.
  // Move focus out in capture phase first, otherwise Chrome warns when aria-hidden=true
  // is applied to an ancestor of the focused close button.
  modal.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    if (target?.closest?.('[data-beatline-member-close]')) moveFocusOut();
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains('is-dismissed')) moveFocusOut();
  }, true);
}

export function applyBackendStatePatches(config: BeatLineViteConfig): void {
  installQrVisibility(config);
  installAuthMarkup(config);
  installAuthGateController(config);
  installMemberModalConfig(config);
  installMemberModalFocusSafety();
}
